import { config } from '../config';
import { FormFieldType } from '../typings';
import type { FormField } from '../typings';
import {
  FORM_FIELD_CHILD_LIST_KEYS,
  FORM_FIELD_CHILD_SINGLE_KEYS,
} from './formFieldChildKeys';
import { getBaseFieldId } from './getBaseFieldId';

export type OrderedValidationError = {
  name: string;
  label: string;
};

/**
 * A row for an error the field tree cannot reach, because whatever raises it renders
 * outside the tree — the sign document below it, today. `name` still has to be a DOM
 * id something renders, or `jumpToField` resolves nothing.
 */
export type TrailingValidationError = OrderedValidationError & {
  errorKey: string;
};

const INDEXED_PREFIX_SEPARATOR = ' – ';

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/** Label fallback shared by node types with a human `title` but no `question` (groups, arrays). */
const titleOrName = (record: Record<string, unknown>): string | undefined =>
  asString(record.title) || asString(record.name);

/** How the summary names one entry of a repeating group: its entry title, else its label and 1-based index. */
const entryName = (
  record: Record<string, unknown>,
  label: string,
): string | undefined =>
  typeof record.title === 'string' && typeof record.index === 'number'
    ? asString(record.entryTitle) || `${label} ${record.index + 1}`
    : undefined;

/** A validation whose error lives on a synthetic Formik key instead of the field's own `name`. */
type SyntheticErrorRule = {
  types: FormFieldType[];
  suffix: string;
  /**
   * Owner of the error key, when it differs from the node's own `fieldId` — e.g. an
   * array-wide rule keyed on the collection's base id rather than one instance's.
   * Setting this also marks the rule collection-scoped, so `emitRow` never indexes
   * its row: a rule that wants per-instance indexing must leave this unset.
   */
  errorKeyId?: (fieldId: string) => string;
  label: (record: Record<string, unknown>) => string | undefined;
};

/**
 * Invariant: every rule's row is emitted against the node's own `fieldId` (see
 * `emitRow`), which must be a DOM id some presenter actually renders via
 * `id={fieldId}` — `jumpToField` resolves it with `getElementById` and fails
 * silently otherwise. Today that's FilePresenter, FieldsGroupPresenter,
 * ArrayPresenter, QuestionWithDetailsPresenter, ConditionalFieldsGroupPresenter,
 * CheckboxGroupPresenter, and
 * StagedFieldsGroupPresenter. A rule for a new type only helps once its
 * presenter carries that id too.
 */
const SYNTHETIC_ERROR_RULES: SyntheticErrorRule[] = [
  {
    types: [FormFieldType.QuestionWithDetails],
    suffix: config.requiredFieldSuffix,
    label: record => asString(record.question),
  },
  {
    // Registered by their initialState modules (generateValidations.ts); FieldsGroup
    // never calls generateValidations for `_required`.
    // ConditionalFieldsGroup needs no entry: it validates its condition field's own
    // value, so `emitRow` already reaches it through that field's name and label.
    types: [FormFieldType.Array, FormFieldType.StagedFieldsGroup],
    suffix: config.requiredFieldSuffix,
    label: titleOrName,
  },
  {
    types: [FormFieldType.File],
    suffix: config.minFilesCountFieldSuffix,
    label: record => asString(record.label),
  },
  {
    // Registered by fieldsGroup/initialState.ts for a required address choice.
    types: [FormFieldType.FieldsGroup],
    suffix: config.addressLinkFieldSuffix,
    label: record =>
      asString((record.address as { prompt?: unknown } | undefined)?.prompt) ||
      titleOrName(record),
  },
  {
    // registerGroupRestrictionValidations is called by all four group/array
    // initialState modules — see createAtLeastOneChildWithValueYup.ts. Order
    // matches GROUP_RESTRICTION_SUFFIXES and GroupRestrictionErrors' render
    // order (with-value slot before answered-yes slot). ConditionalFieldsGroup
    // is deliberately absent: it registers this suffix under each branch's own
    // groupFieldId (conditionalFieldsGroup/initialState.ts), never its own
    // fieldId, so this entry could never match for that type — see
    // emitGroupRestrictionRows for how its restrictions actually reach the summary.
    types: [
      FormFieldType.FieldsGroup,
      FormFieldType.Array,
      FormFieldType.StagedFieldsGroup,
    ],
    suffix: config.atLeastOneChildWithValueFieldSuffix,
    label: titleOrName,
  },
  {
    types: [
      FormFieldType.FieldsGroup,
      FormFieldType.Array,
      FormFieldType.StagedFieldsGroup,
    ],
    suffix: config.atLeastOneChildAnsweredYesFieldSuffix,
    label: titleOrName,
  },
  {
    types: [FormFieldType.Array],
    suffix: config.atLeastOneCompleteFieldSuffix,
    errorKeyId: getBaseFieldId,
    label: record => asString(record.title),
  },
];

/**
 * Suffixes a group restriction can register — the same two `GroupRestrictionErrors`
 * checks for one restriction, in the order it renders them.
 */
const GROUP_RESTRICTION_SUFFIXES = [
  config.atLeastOneChildWithValueFieldSuffix,
  config.atLeastOneChildAnsweredYesFieldSuffix,
];

/**
 * Emits a row for a node's own `name` error when present — which is then
 * authoritative for that node, e.g. a File's own error and its `_minFilesCount`
 * both firing is one failure surfacing twice, not two — then, failing that, for a
 * `restrictionErrorKey` stamped on the node by a group's RequiredWhenValue rule —
 * otherwise one row per
 * `SYNTHETIC_ERROR_RULES` entry that matches. A node can genuinely fail more
 * than one rule at once (its own `_atLeastOneChildWithValue` and the array-wide
 * `_atLeastOneComplete`, say), so this can push more than one row; the
 * per-error-key `emitted` dedupe is what stops duplicates and repeats across
 * instances, not a one-row-per-node limit. A second, row-content dedupe (same
 * `emitted` set, namespaced `row:`) additionally collapses two different rules
 * that land on an identical `{ name, label }` — e.g. a group's two restriction
 * slots co-failing at once, since neither answering Yes nor filling in a value
 * happened — without suppressing rules that legitimately differ in label (an
 * array instance's own restriction vs. the array-wide completeness rule).
 */
function emitRow(
  record: Record<string, unknown>,
  errors: Record<string, string | undefined>,
  prefix: string,
  out: OrderedValidationError[],
  emitted: Set<string>,
): void {
  const name = asString(record.name);
  const label = asString(record.label);

  if (name && label && errors[name] !== undefined) {
    // Namespaced so a plain field name can never collide with a synthetic error key.
    const token = `name:${name}`;
    if (!emitted.has(token)) {
      emitted.add(token);
      out.push({ name, label: `${prefix}${label}` });
    }
    // The node's own name error is authoritative for that node — see doc comment.
    return;
  }

  // A RequiredWhenValue restriction is keyed on the guarding group, but
  // `stampGuardedFieldError` stamps that key onto the single child it guards and
  // `SingleFieldPresenter` renders the message beside that child — so the row
  // belongs to the child, whose own name, label and id are already correct. A
  // group-keyed rule here would instead jump to a container the message is not in.
  const restrictionErrorKey = asString(record.restrictionErrorKey);

  if (
    restrictionErrorKey &&
    name &&
    label &&
    errors[restrictionErrorKey] !== undefined
  ) {
    const keyToken = `key:${restrictionErrorKey}`;

    if (!emitted.has(keyToken)) {
      emitted.add(keyToken);
      out.push({ name, label: `${prefix}${label}` });
    }

    return;
  }

  const fieldId = asString(record.fieldId);
  if (!fieldId) return;

  for (const rule of SYNTHETIC_ERROR_RULES) {
    if (!rule.types.includes(record.type as FormFieldType)) continue;

    // A collection-scoped rule (errorKeyId set) belongs to the collection's
    // first instance alone, matching the explicit `index === 0` checks in
    // ArrayPresenter and array/initialState.ts — not merely whichever
    // instance this tree walk happens to visit first.
    if (
      rule.errorKeyId &&
      typeof record.index === 'number' &&
      record.index !== 0
    ) {
      continue;
    }

    const owner = rule.errorKeyId ? rule.errorKeyId(fieldId) : fieldId;
    const errorKey = `${owner}${rule.suffix}`;
    const keyToken = `key:${errorKey}`;

    if (errors[errorKey] === undefined || emitted.has(keyToken)) continue;

    // Fall back to the node's own fieldId so a blank title/name/label never drops
    // the row silently — a row labelled with a raw id is still clickable, which is
    // the whole point of this table; an omitted row is not recoverable by the reader.
    const ruleLabel = rule.label(record) || fieldId;

    emitted.add(keyToken);

    // Repeated instances share a title, so name the instance — except for
    // collection-scoped rules (errorKeyId set), where naming one instance
    // would misrepresent the rule.
    const indexed =
      (!rule.errorKeyId && entryName(record, ruleLabel)) || ruleLabel;

    const rowLabel = `${prefix}${indexed}`;
    // Two different rules on the same node can render an identical row (a
    // group's two restriction slots co-failing, say) — the row itself is the
    // real identity for this dedupe, not the error key that produced it.
    const rowToken = `row:${fieldId}:${rowLabel}`;
    if (emitted.has(rowToken)) continue;
    emitted.add(rowToken);

    out.push({ name: fieldId, label: rowLabel });
    // No early return: table order, so a node failing more than one rule
    // (its own restriction and the array-wide completeness rule, say) gets a
    // row per rule instead of only the first match.
  }
}

/**
 * Emits one row per failing entry of a `groupRestrictions` collection — today only
 * `ConditionalFieldsGroupFormField`. Each entry registers its validation under its
 * own `groupFieldId` (a flattened branch group's id), not the owning node's
 * `fieldId` (`conditionalFieldsGroup/initialState.ts`), so `emitRow`'s fieldId-keyed
 * rules can never reach it — this is a separate, collection-scoped path rather than
 * another `SYNTHETIC_ERROR_RULES` entry.
 *
 * Invariant: each row's `name` is the entry's `groupFieldId`, which must be a DOM id
 * some presenter renders — `ConditionalFieldsGroupPresenter` wraps each
 * `GroupRestrictionErrors` in an element carrying that id for exactly this.
 */
function emitGroupRestrictionRows(
  record: Record<string, unknown>,
  errors: Record<string, string | undefined>,
  prefix: string,
  out: OrderedValidationError[],
  emitted: Set<string>,
): void {
  const groupRestrictions = record.groupRestrictions;
  if (!Array.isArray(groupRestrictions)) return;

  // A group-level failure is not one field — label with the owning node's own
  // title, falling back to its name, same policy as the table's group rules
  // (titleOrName). Two entries can legitimately share this label; that's
  // acceptable rather than inventing disambiguation.
  const title = titleOrName(record);

  for (const entry of groupRestrictions) {
    if (!entry || typeof entry !== 'object') continue;

    const groupFieldId = asString(
      (entry as Record<string, unknown>).groupFieldId,
    );
    if (!groupFieldId) continue;

    for (const suffix of GROUP_RESTRICTION_SUFFIXES) {
      const errorKey = `${groupFieldId}${suffix}`;
      const keyToken = `key:${errorKey}`;

      if (errors[errorKey] === undefined || emitted.has(keyToken)) continue;

      emitted.add(keyToken);

      const rowLabel = `${prefix}${title || groupFieldId}`;
      // Both restriction slots can co-fail on one branch at once (Critical 1)
      // — dedupe the row itself so that doesn't surface as two identical rows.
      const rowToken = `row:${groupFieldId}:${rowLabel}`;
      if (emitted.has(rowToken)) continue;
      emitted.add(rowToken);

      out.push({ name: groupFieldId, label: rowLabel });
    }
  }
}

function walk(
  node: unknown,
  errors: Record<string, string | undefined>,
  prefix: string,
  out: OrderedValidationError[],
  emitted: Set<string>,
): void {
  if (!node || typeof node !== 'object') {
    return;
  }

  const record = node as Record<string, unknown>;

  emitRow(record, errors, prefix, out, emitted);
  emitGroupRestrictionRows(record, errors, prefix, out, emitted);

  // Repeated instances (array items, staged groups) carry both `title` and
  // `index`; their name prefixes their descendants, so duplicate labels (e.g.
  // "First Name" for every owner) are disambiguated in the list.
  const named = entryName(record, String(record.title));
  const nextPrefix =
    named !== undefined ? `${named}${INDEXED_PREFIX_SEPARATOR}` : prefix;

  for (const key of FORM_FIELD_CHILD_LIST_KEYS) {
    const value = record[key];
    if (Array.isArray(value)) {
      value.forEach(child => walk(child, errors, nextPrefix, out, emitted));
    }
  }

  // An Office Hours field keys its days by DayOfWeek instead of holding a child
  // array, so neither traversal above reaches them. Each day's own name prefixes
  // its fields, so a row reads "Monday – To" rather than a bare, repeated "To".
  const days = record.days;

  if (days && typeof days === 'object' && !Array.isArray(days)) {
    for (const day of Object.values(days as Record<string, unknown>)) {
      if (!day || typeof day !== 'object') continue;

      const dayRecord = day as Record<string, unknown>;
      const dayName = asString(dayRecord.name);
      const dayPrefix = dayName
        ? `${nextPrefix}${dayName}${INDEXED_PREFIX_SEPARATOR}`
        : nextPrefix;

      if (Array.isArray(dayRecord.fields))
        dayRecord.fields.forEach(child =>
          walk(child, errors, dayPrefix, out, emitted),
        );
    }
  }

  for (const key of FORM_FIELD_CHILD_SINGLE_KEYS) {
    walk(record[key], errors, nextPrefix, out, emitted);
  }
}

/** Walks the section's field tree in visual order, returning every field with a current error. */
export function getOrderedValidationErrors(
  formFieldGroups: FormField[],
  errors: Record<string, string | undefined>,
  /** Appended after the tree, in the order given — they render below it. */
  trailingErrors: TrailingValidationError[] = [],
): OrderedValidationError[] {
  const out: OrderedValidationError[] = [];
  const emitted = new Set<string>();
  formFieldGroups.forEach(field => walk(field, errors, '', out, emitted));

  for (const { errorKey, name, label } of trailingErrors) {
    const keyToken = `key:${errorKey}`;

    if (errors[errorKey] === undefined || emitted.has(keyToken)) continue;

    emitted.add(keyToken);
    out.push({ name, label });
  }

  return out;
}
